import { Toaster } from 'sonner';
import { PLAID_OAUTH_PATH } from './utils/plaidLink';
import { PlaidOAuthReturn } from './components/ar/PlaidOAuthReturn';
import { MfaChallengeScreen } from './components/auth/MfaChallengeScreen';
import { lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AccessProvider } from './context/AccessContext';
import { VersionWatcher } from './components/shared/VersionWatcher';
import { LoginPage } from './components/LoginPage';
import { SetInitialPasswordScreen } from './components/auth/SetInitialPasswordScreen';
import { Loader as Loader2 } from 'lucide-react';
import { isApplyPath } from './components/apply/applyPath';
import { carrierPageTokenOf } from '../supabase/functions/make-server-e3a97ae5/_shared/freight-carrier-page.ts';
import { lazyWithReload } from './utils/lazyWithReload';

// The authenticated app (26 providers + every view) is a lazy chunk so the
// login page — the only surface PageSpeed/Lighthouse can reach — stays tiny.
const MainApp = lazy(() => import('./MainApp'));

// The public job application (/apply) — its own small chunk, loaded only there.
const ApplyPage = lazyWithReload(() => import('./components/apply/ApplyPage'));
// The carrier's own quote page (/freight-quote/<token>) — its own small chunk too.
const CarrierQuotePage = lazyWithReload(() => import('./components/freight/carrier/CarrierQuotePage'));

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-gray-400" />
        <p className="text-sm text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

export type JobPhase =
  | 'pre-bid'
  | 'bidding'
  | 'sales'
  | 'drafting'
  | 'pm'                   // v2 — unified PM phase (replaces 'project-management')
  | 'project-management'   // legacy — kept for back-compat with seeded data
  | 'plant'                // v2 — unified plant phase (replaces color-sample/mold-*/packaging)
  | 'color-sample'
  | 'mold-programming'
  | 'mold-building'
  | 'production'
  | 'packaging'
  | 'ready-shipping'
  | 'ar'                   // v2 — short form (replaces 'accounts-receivable')
  | 'accounts-receivable'  // legacy — kept for back-compat
  | 'declined'             // v2 — dead-end terminal
  | 'lost'                 // v2 — dead-end terminal
  | 'completed';

export interface Job {
  id: string;
  name: string;
  nickname?: string | null;
  jobNumber: string;
  customer: string;
  phase: JobPhase;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  bidDueDate: string;
  progress: number;
  bidPrice: number;
  description: string;
  assignedTo: string[];
  details: Record<string, any>;
  status?: 'active' | 'completed' | 'on-hold';
  /** Disposable test fixture (jobs.is_test). Renders on the boards wearing a
   *  TEST chip, pinned below every real card, and left out of the pipeline
   *  totals and the sales wake engine. NOT the same as the legacy demo-seed
   *  flag (`details.isSeed` / a 'SEED-' job number), which HIDES a card once
   *  its lane has real deals — a test deal must always show. */
  isTest?: boolean;

  // MOS v2 — Priority & scheduling (Phase A)
  globalPriority?: number | null;
  requestedDeliveryDate?: string | null;
  requestedDateType?: 'Soft' | 'Hard' | null;
  anticipatedDeliveryDate?: string | null;
  baselineAnticipatedDate?: string | null;
  driftDays?: number | null;
  deliveryStatus?: 'OnTrack' | 'Tight' | 'AtRisk' | 'NoDate';
  communicatedWindow?: string | null;
  area?: number | null;
  jobSource?: string | null;

  // MOS v2 — Plant pipeline + drift tracking (P-1/P-3)
  driftStatus?: 'on-track' | 'drifting' | 'flagged' | 'accepted-drift' | null;
  originalPromiseDate?: string | null;
  currentPromiseDate?: string | null;
  plantEntryAt?: string | null;

  // MOS v2 — Gate timestamps (Phase A)
  awardedAt?: string | null;
  draftingEnteredAt?: string | null;
  /** Drafting board membership — a human (or a genuine award) put it on the board. */
  draftingBoardedAt?: string | null;
  /** Award intake: PM signed off the new-job entry (clears the gray drafting card). */
  awardVerifiedAt?: string | null;
  /** Award intake: the award survey was submitted. */
  awardIntakeAt?: string | null;
  /** Award pending: PO detected / verbal award — human confirms, then clicks Won. */
  awardPendingAt?: string | null;
  awardPendingSource?: 'po_email' | 'manual' | null;
  drawingsApprovedAt?: string | null;
  productionEnteredAt?: string | null;
  shippedAt?: string | null;
  /** Live/archive split: null = live working set; ISO date = archived (closed/historical). */
  archivedAt?: string | null;
  /** Most recent revive from the archive (trigger-stamped) — boards floor lane-age clocks on it. */
  unarchivedAt?: string | null;

  // MOS v2 — Sales pipeline (Phase B)
  preBidProdGrade?: string | null;
  /** Preliminary job size (small|medium|large|xl) — the eyeballed scope that
      stands in for bidPrice until the estimate sets a real one. See
      src/utils/jobSize.ts. */
  prelimSize?: string | null;
  salesStage?: string | null;
  ghostProbability?: number | null;
  competitorIntel?: Record<string, unknown> | null;
  declineReason?: string | null;
  declinedAt?: string | null;
  /** Who executed the pass (employees TEXT id) — rides every jobs payload. */
  declinedBy?: string | null;
  /** Canonical pass reason (src/utils/passReasons.ts), stamped at flag time. */
  declineReasonCode?: string | null;
  /** nothing_to_bid | our_call | we_couldnt | other — which KIND of no. */
  declineCategory?: string | null;
  /** When the no-bid notice went out. Null = not sent (or the send failed). */
  noBidNoticeSentAt?: string | null;
  noBidNoticeTo?: Array<{ name: string; email: string; company: string | null }> | null;

  // MOS Sales Workbench Slice 1
  salesStatus?: string | null;
  salesStageEnteredAt?: string | null;
  /** When the deal was called lost. Stamped by trg_deal_loss_stamps on the
      status transition and cleared when the deal is reopened — the decision
      clock Pipeline Health and the estimator's outcome memory both read. */
  lostAt?: string | null;
  /** Lost AFTER award (Mark, 2026-09-22): the customer walked away from an
      awarded job. sales_status stays 'won' — the AR board and the archive
      trigger both key on it — so this record is the fact. snake_case like
      the ar_* block: useArLanes reads it off the raw Job and off Realtime
      UPDATE payloads alike. The items are the confirmed checklist. */
  lost_after_award_at?: string | null;
  lost_after_award_value?: number | string | null;
  lost_after_award_items?: unknown;
  /** Who called it (employees TEXT id) — the one half a trigger can't know. */
  lostBy?: string | null;
  /** Where the card sat before it was booked (won/lost/rebid) — read by Undo/Reactivate, cleared on restore. */
  preBookedSnapshot?: { stage?: string | null; status?: string | null; stageEnteredAt?: string | null } | null;
  // 'mason' is a frozen DB wire value meaning Buyer — do not rename (Buyer merge 2026-07-14)
  buyerType?: 'mason' | 'gc' | 'unknown' | null;
  architectFirm?: string | null;
  architectContactId?: string | null;
  architectNa?: boolean | null;
  /** Explicit "architect unknown for now" answer — satisfies the pre-bid Architect check. */
  architectUnknown?: boolean | null;
  gcCompanyId?: string | null;
  gcPmContactId?: string | null;
  gcNa?: boolean | null;
  /** GC provenance — 'itb_suggested' renders the Suggested chip + Confirm;
      confirming writes 'manual'. Never gates movement or tasks. */
  gcCompanySource?: string | null;
  /** Nightly GC watch: set when the Monday archive disagrees with the card's GC. */
  gcCrosscheck?: { monday_says?: string; monday_company_id?: string; bidders_back?: boolean; checked_at?: string } | null;
  /** Buyer confirmation stamp — a filled buyer without this is a belief, not established. */
  buyerEstablishedAt?: string | null;
  /** GC confirmation stamp — the same rule on the GC row: a filled GC without
      this is who we HEARD got awarded, not who told us they did. */
  gcConfirmedAt?: string | null;
  // legacy — frozen, read-fallback only (Buyer merge 2026-07-14); new writes go to buyerCompanyId/buyerContactId
  masonCompanyId?: string | null;
  masonPmContactId?: string | null;
  masonNa?: boolean | null;
  buyerCompanyId?: string | null;
  buyerContactId?: string | null;
  bidValidThroughDate?: string | null;
  nextFollowUpDate?: string | null;
  lastContactAttemptAt?: string | null;
  contactAttemptCount?: number | null;
  estimatedProductionDays?: number | null;
  /** Casting days + the setup block (v_job_production_days.ranking_days) —
   *  what the desire score divides by, shared with SQL's sales_value_core so
   *  the sweep's pick and the lane's order use one number. */
  rankingProductionDays?: number | null;

  // MOS Sales Workbench Slice 1 v2.0
  city?: string | null;
  state?: string | null;
  // Structured location from the Google-style search (finer geography for analytics)
  zip?: string | null;
  county?: string | null;
  lat?: number | null;
  lng?: number | null;
  distanceMi?: number | null;      // driving miles from the plant (freight)
  distanceSource?: 'drive' | 'unavailable' | null;
  profitMargin?: number | null;
  isHotDeal?: boolean | null;
  hotDealSetAt?: string | null;
  hotDealSetBy?: string | null;
  hotDealReason?: string | null;
  priceCheckStatus?: 'current' | 'awaiting' | 'stale' | null;
  priceCheckCompletedAt?: string | null;
  lastActivityAt?: string | null;

  // MOS v2 — Estimating board (gate-input columns + bump state for the
  // derived five-lane board; carried on the lightweight load)
  plansStoredAt?: string | null;
  /** Spec book stored in MOS Drive (job_files category 'specs') — trigger-maintained. */
  specStoredAt?: string | null;
  /** SRF send stamp — the ONE record of a sent SRF on an open-spec job, where
      spec_status keeps 'open_spec' (no srf_* token exists for that path). */
  srfSubmittedAt?: string | null;
  plansUploadedToCctoAt?: string | null;
  bidApprovedAt?: string | null;
  takeoffComplete?: boolean | null;
  seniorReviewCompletedAt?: string | null;
  readyForSeniorReviewAt?: string | null;
  readyForSeniorReviewBy?: string | null;
  /** How the estimator wants the review done: 'confident' | 'deep_dive'. */
  readyForSeniorReviewConfidence?: string | null;
  priorityBumpAt?: string | null;
  priorityBumpReason?: string | null;
  priorityBumpBy?: string | null;

  // MOS v2 — Bid Revision loop (Sales → Estimating → Sales). 'requested'/
  // 'estimated' = revision out (Sales card hidden, Estimate lane pinned);
  // 'returned' = estimator couldn't complete it — card back on the Sales
  // board wearing the violet needs-info frame until resubmitted;
  // 'returned_intake' = early-stage (Find GC/Find Buyer) deal bounced to
  // the Estimating board's Intake lane for the missing info instead.
  // 'ready_to_send' = revision done; card back on Sales wearing the frame
  // until the completion popup sends the update and closes the request.
  bidRevisionStatus?: 'requested' | 'estimated' | 'sending' | 'sent' | 'returned' | 'returned_intake' | 'ready_to_send' | null;

  /** Value engineering — a passed job reopened for a second look (⋯ menu →
      "Reassess for Value Engineering"). 'open' pins the Estimating board's
      Estimate lane + paints the VE frame until the estimator marks the
      re-price done ('estimated'). See estimating/board/valueEngineering.ts. */
  valueEngineeringStatus?: 'open' | 'estimated' | null;
  /** What could change to make the job work — the estimator's brief. */
  valueEngineeringNote?: string | null;
  valueEngineeringAt?: string | null;
  /** Who reopened it (employees TEXT id). */
  valueEngineeringBy?: string | null;

  // MOS v2 — Drafting board
  /** Drawing revision label (V1, V2…). Shown as a badge on the drafting card;
      auto-increments on resubmit once the submit action exists. */
  shopVersion?: string | null;
}

export interface Contact {
  id: string;
  name: string;
  company: string;
  // Mock-only union; 'mason' is a legacy value displayed as Buyer (Buyer merge 2026-07-14)
  type: 'architect' | 'general-contractor' | 'mason';
  email: string;
  phone: string;
  state?: string;
  address?: string;
  notes?: string;
}

function AuthenticatedApp() {
  const { user, loading, requiresPasswordSetup, mfaState } = useAuth();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <LoginPage />;
  }

  // A verified authenticator exists but this session is still aal1. Nothing
  // else renders until the code is in — including the OAuth return below,
  // which finishes a bank connection and is exactly what a stolen password
  // should not reach.
  //
  // The code comes BEFORE the set-a-password screen (Drew, 2026-10-08). A
  // reset link signs you in at aal1, and Supabase refuses a password change
  // from an aal1 session once a verified factor exists — so with the password
  // screen first, someone with two-factor on could never finish a reset: the
  // save failed and nothing on the screen asked for the code.
  if (mfaState === 'checking') {
    return <FullScreenLoader />;
  }
  if (mfaState === 'challenge') {
    return <MfaChallengeScreen />;
  }

  // Invited users (and password-reset users) land here with a live session
  // but no real password set. Block the rest of the app until they pick one.
  if (requiresPasswordSetup) {
    return <SetInitialPasswordScreen />;
  }

  // Back from an OAuth bank. Handled here, after the session has hydrated but
  // before the provider tree: the exchange needs a real user token, and none
  // of the rest of MOS has anything to do with finishing a Plaid handshake.
  if (window.location.pathname === PLAID_OAUTH_PATH) {
    return <PlaidOAuthReturn />;
  }

  // Authenticated: load the full provider tree + app as a separate chunk.
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <MainApp />
    </Suspense>
  );
}

function App() {
  // The job application is the one MOS page anyone may open. It renders ahead
  // of the auth provider, so it never asks for a sign-in and none of the
  // app's chrome (nav, update card, toasts) appears around it — the same page
  // whether or not this browser is signed in to MOS. It calls one public
  // route with the anon key and reads nothing of the signed-in app.
  if (isApplyPath(window.location.pathname)) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <ApplyPage />
      </Suspense>
    );
  }

  // The carrier's quote page (2026-10-20) is the second: a carrier opens it
  // from the button in a quote request, never signed in. Its token is the
  // key; it reads one carrier's request through two public routes.
  if (carrierPageTokenOf(window.location.pathname)) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <CarrierQuotePage />
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      {/* Immediately under auth: everything downstream — the nav, every board,
          every <Money> — asks what this person may reach, and the answer is
          resolved from their access profile before the first paint. */}
      <AccessProvider>
      <AuthenticatedApp />
      {/* Toasts lift above the update card when one is showing — VersionWatcher
          publishes the corner it occupies as --mos-update-card-space, and the
          card caps itself at 100vh-8rem so the lifted stack still fits. With no
          card up, max() leaves sonner's own 24px / 16px insets untouched. */}
      <Toaster
        position="bottom-right"
        richColors
        closeButton
        offset={{ bottom: 'max(24px, var(--mos-update-card-space, 0px))' }}
        mobileOffset={{ bottom: 'max(16px, var(--mos-update-card-space, 0px))' }}
      />
      <VersionWatcher />
      </AccessProvider>
    </AuthProvider>
  );
}

export default App;